import React from "react"
import {
  IconAbc,
  IconLetterCaseToggle,
  IconLetterS,
  IconFeather,
  IconMoon,
  IconMapPin,
  IconAlphabetLatin,
} from "@tabler/icons-react"
import type { GrammarEntry } from "./grammar_glossary"
import { TransformList, WordTransformProps } from "./word_transform"

const NAV_ICON_SIZE = 24
const NAV_ICON_STROKE_WIDTH = 2

/**
 * The Irish pronunciation guide, rendered as chart tables (rich JSX carried on the slide `content`).
 * Data adapted from the "Sounds & Spelling of Irish / Fuaimeanna agus Litriú na Gaeilge" handout by
 * Ray Mac Mánais and Karen Reshkin. Written sounds are English-approximate, not exact, and vary by
 * dialect. Credit the source if this is surfaced prominently.
 */

interface Sound {
  /** The letter or combination, e.g. "á", "ao", "slender s". */
  label: string
  ipa: string
  /** Irish example words. */
  irish: string
  /** English-approximate sound(s). */
  english: string
  /** Dialect or usage note. */
  note?: string
}

const SoundTable: React.FC<{ rows: Sound[] }> = ({ rows }) => (
  <table className="w-full border-collapse text-sm" data-testid="sound-table">
    <tbody>
      {rows.map((row) => (
        <tr key={row.label} className="border-t border-secondary-100 align-top">
          <td className="whitespace-nowrap py-1.5 pr-3 font-semibold italic">
            {row.label}
          </td>
          <td className="whitespace-nowrap py-1.5 pr-3 font-mono text-xs text-muted-foreground">
            {row.ipa}
          </td>
          <td className="py-1.5 pr-3 italic">{row.irish}</td>
          <td className="py-1.5 text-muted-foreground">
            {row.english}
            {row.note && <span className="block text-xs">{row.note}</span>}
          </td>
        </tr>
      ))}
    </tbody>
  </table>
)

interface MutationSound {
  ipa: string
  irish: string
  english: string
  note?: string
}
interface Mutation {
  label: string
  broad: MutationSound
  slender: MutationSound
}

const MutationCell: React.FC<{ sound: MutationSound }> = ({ sound }) => (
  <div>
    <span className="font-mono text-xs text-muted-foreground">{sound.ipa}</span>
    <div className="italic">{sound.irish}</div>
    <div className="text-muted-foreground">{sound.english}</div>
    {sound.note && (
      <div className="text-xs text-muted-foreground">{sound.note}</div>
    )}
  </div>
)

const MutationTable: React.FC<{ rows: Mutation[] }> = ({ rows }) => (
  <table
    className="w-full border-collapse text-sm"
    data-testid="mutation-table"
  >
    <thead>
      <tr className="text-xs text-muted-foreground">
        <th className="py-1 text-left font-medium" />
        <th className="py-1 pr-3 text-left font-medium">Broad (a, o, u)</th>
        <th className="py-1 text-left font-medium">Slender (e, i)</th>
      </tr>
    </thead>
    <tbody>
      {rows.map((row) => (
        <tr key={row.label} className="border-t border-secondary-100 align-top">
          <td className="py-1.5 pr-3 font-semibold">{row.label}</td>
          <td className="py-1.5 pr-3">
            <MutationCell sound={row.broad} />
          </td>
          <td className="py-1.5">
            <MutationCell sound={row.slender} />
          </td>
        </tr>
      ))}
    </tbody>
  </table>
)

interface Eclipsis {
  label: string
  /** How the word starts to look, e.g. "mb". */
  becomes: string
  sounds: {
    context?: string
    ipa: string
    irish: string
    english: string
    note?: string
  }[]
}

const EclipsisTable: React.FC<{ rows: Eclipsis[] }> = ({ rows }) => (
  <table
    className="w-full border-collapse text-sm"
    data-testid="eclipsis-table"
  >
    <thead>
      <tr className="text-xs text-muted-foreground">
        <th className="py-1 text-left font-medium" />
        <th className="py-1 pr-3 text-left font-medium">Becomes</th>
        <th className="py-1 text-left font-medium">Sound</th>
      </tr>
    </thead>
    <tbody>
      {rows.map((row) => (
        <tr key={row.label} className="border-t border-secondary-100 align-top">
          <td className="py-1.5 pr-3 font-semibold">{row.label}</td>
          <td className="whitespace-nowrap py-1.5 pr-3 font-semibold italic">
            {row.becomes}
          </td>
          <td className="py-1.5">
            {row.sounds.map((sound, index) => (
              <div key={index} className="mb-0.5">
                {sound.context && (
                  <span className="text-xs text-muted-foreground">
                    {sound.context}:{" "}
                  </span>
                )}
                <span className="font-mono text-xs text-muted-foreground">
                  {sound.ipa}
                </span>{" "}
                <span className="italic">{sound.irish}</span>{" "}
                <span className="text-muted-foreground">({sound.english})</span>
                {sound.note && (
                  <span className="text-xs text-muted-foreground">
                    {" "}
                    {sound.note}
                  </span>
                )}
              </div>
            ))}
          </td>
        </tr>
      ))}
    </tbody>
  </table>
)

const VOWELS: Sound[] = [
  { label: "a", ipa: "/a/", irish: "hata, mac", english: "father, spa" },
  { label: "á", ipa: "/ɑ:/", irish: "tá, bád", english: "call, far" },
  { label: "e", ipa: "/ɛ/", irish: "te, ceist", english: "tell, get" },
  { label: "é", ipa: "/e:/", irish: "cé, éist", english: "bait, pay" },
  { label: "i", ipa: "/ɪ/", irish: "ith, bille", english: "in, hit" },
  {
    label: "í",
    ipa: "/i:/",
    irish: "Síle, trí, íseal",
    english: "tree, mean, he",
  },
  { label: "o", ipa: "/ɔ/", irish: "olc, moch", english: "tug, wonder" },
  { label: "ó", ipa: "/o:/", irish: "ól, bóthar", english: "roll, toe, road" },
  { label: "u", ipa: "/ʌ/", irish: "dubh, punt", english: "good, pull" },
  { label: "ú", ipa: "/u:/", irish: "tú, cúpla", english: "too, rude" },
]

const COMBINATIONS: Sound[] = [
  { label: "ae", ipa: "/e/", irish: "tae, laethanta", english: "day" },
  {
    label: "ao",
    ipa: "/i/",
    irish: "daor, saol",
    english: "feel",
    note: "Munster /e/, as in quail",
  },
  { label: "aoi", ipa: "/i/", irish: "Aoife, saoirse", english: "me" },
  { label: "ei", ipa: "/ɛ/", irish: "eisean, ceimic", english: "meh" },
  { label: "eo", ipa: "/o/", irish: "Eoin, ceol, beo", english: "no" },
  { label: "ia", ipa: "/iə/", irish: "bia, pian", english: "deal" },
  { label: "ua", ipa: "/uə/", irish: "fuath", english: "tool" },
  {
    label: "io, oi, ui",
    ipa: "/ɪ/",
    irish: "briosca, coileach, duine",
    english: "fit (usually)",
  },
  {
    label: "ea",
    ipa: "/æ/, /a/, /iɑ:/",
    irish: "bean, fear, geall",
    english: "apple, shot, yawn",
  },
]

const SLENDER_CONSONANTS: Sound[] = [
  {
    label: "slender s",
    ipa: "/ʃ/",
    irish: "Síle, mise, anois",
    english: "shoe, fish",
  },
  {
    label: "slender t",
    ipa: "/tʃ/",
    irish: "te, leite, Cait",
    english: "chin, chai",
  },
  {
    label: "slender d",
    ipa: "/dʲ/",
    irish: "Dia, cuidiú, leid",
    english: "Judge, jiffy",
  },
  {
    label: "slender l",
    ipa: "/lʲ/",
    irish: "líne, bileog, cáil",
    english: "million, William",
  },
  {
    label: "slender n",
    ipa: "/nʲ/",
    irish: "níl, sloinne",
    english: "sinew, minion",
  },
]

const SEIMHIU: Mutation[] = [
  {
    label: "b",
    broad: {
      ipa: "/w/",
      irish: "bhain, gabh",
      english: "wow, tower",
      note: "/v/ in Munster",
    },
    slender: { ipa: "/v/", irish: "a bheith, sibh", english: "village, eve" },
  },
  {
    label: "c",
    broad: {
      ipa: "/x/",
      irish: "chomh, nach",
      english: "loch (Scottish), Nacht (German)",
    },
    slender: { ipa: "/ç/", irish: "an cheist, deich", english: "a strong hue" },
  },
  {
    label: "d",
    broad: { ipa: "/ɣ/", irish: "dhún", english: "no English equivalent" },
    slender: { ipa: "/j/", irish: "a Dhia", english: "yell" },
  },
  {
    label: "f",
    broad: { ipa: "silent", irish: "an fhuaim, m'fhoireann", english: "" },
    slender: { ipa: "silent", irish: "an fhéile, an fhiacail", english: "" },
  },
  {
    label: "g",
    broad: {
      ipa: "/ɣ/",
      irish: "ghasúr, ghlór",
      english: "no English equivalent",
    },
    slender: { ipa: "/j/", irish: "mo ghile, oighear", english: "yell" },
  },
  {
    label: "m",
    broad: {
      ipa: "/w/",
      irish: "amháin, domhan",
      english: "wow, tower",
      note: "/v/ in Munster",
    },
    slender: { ipa: "/v/", irish: "mo mhéar, nimh", english: "village, eve" },
  },
  {
    label: "p",
    broad: { ipa: "/f/", irish: "sa pholl, phós", english: "follow" },
    slender: { ipa: "/f/", irish: "an pheil", english: "fell" },
  },
  {
    label: "s",
    broad: { ipa: "/h/", irish: "Nollaig shona, mo shaol", english: "honey" },
    slender: { ipa: "/h/", irish: "mo shiopa, ar sheift", english: "hello" },
  },
  {
    label: "t",
    broad: { ipa: "/h/", irish: "mo thóin, tharla", english: "honey" },
    slender: { ipa: "/h/", irish: "trí thine, rithim", english: "hello" },
  },
]

const URU: Eclipsis[] = [
  {
    label: "b",
    becomes: "mb",
    sounds: [{ ipa: "/m/", irish: "ar an mbord", english: "moo" }],
  },
  {
    label: "c",
    becomes: "gc",
    sounds: [{ ipa: "/g/", irish: "i gCorcaigh", english: "goo" }],
  },
  {
    label: "d",
    becomes: "nd",
    sounds: [
      { context: "broad", ipa: "/n/", irish: "i nDoire", english: "not" },
      { context: "slender", ipa: "/nʲ/", irish: "i ndiaidh", english: "sinew" },
    ],
  },
  {
    label: "f",
    becomes: "bhf",
    sounds: [
      {
        context: "broad",
        ipa: "/w/",
        irish: "an bhfuair tú",
        english: "woo",
        note: "/v/ in Munster",
      },
      {
        context: "slender",
        ipa: "/v/",
        irish: "i bhFear Manach",
        english: "vein",
      },
    ],
  },
  {
    label: "g",
    becomes: "ng",
    sounds: [
      { context: "broad", ipa: "/ŋ/", irish: "i nGaillimh", english: "wing" },
      {
        context: "slender",
        ipa: "/ŋʲ/",
        irish: "i ngeall",
        english: "sing your song",
      },
    ],
  },
  {
    label: "p",
    becomes: "bp",
    sounds: [{ ipa: "/b/", irish: "i bPáras", english: "boy" }],
  },
  {
    label: "t",
    becomes: "dt",
    sounds: [
      { context: "broad", ipa: "/d/", irish: "i dTrá Lí", english: "date" },
      { context: "slender", ipa: "/dʲ/", irish: "ár dteach", english: "jet" },
    ],
  },
]

// Brilliant-style worked transforms: green = the added/sounded letter, struck-grey = written but
// silenced. Shown above the detail tables so the rule is visible before the reference.
const URU_TRANSFORMS: WordTransformProps[] = [
  {
    from: "[b]ord",
    to: "[+m][-b]ord",
    fromNote: "a table",
    toNote: "sound only /m/, ‘moo’",
  },
  {
    from: "[c]at",
    to: "[+g][-c]at",
    fromNote: "a cat",
    toNote: "sound only /g/, ‘goo’",
  },
  {
    from: "[p]áras",
    to: "[+b][-p]áras",
    fromNote: "Paris",
    toNote: "sound only /b/, ‘boy’",
  },
]

const SEIMHIU_TRANSFORMS: WordTransformProps[] = [
  { from: "[b]í", to: "b[+h]í", fromNote: "was", toNote: "b + h → /v/, ‘vee’" },
  {
    from: "[c]at",
    to: "c[+h]at",
    fromNote: "cat",
    toNote: "c + h → /x/, as in loch",
  },
  {
    from: "[m]éar",
    to: "m[+h]éar",
    fromNote: "finger",
    toNote: "m + h → /w/~/v/",
  },
]

export const pronunciationGuide: GrammarEntry = {
  title: "Irish pronunciation guide",
  slides: [
    {
      heading: "How the spelling sounds",
      tip: "grug not learn all 40 sounds first. grug pick one dialect, copy a recording, say it out loud. mouth learn faster than head.",
      body: "Irish uses 18 letters to spell over 40 sounds, so the spelling is regular once you learn the rules, but it doesn't map onto English. Consonants come in broad (next to a, o, u) and slender (next to e, i) pairs, and a fada (á é í ó ú) makes a vowel long. The written sounds below are English-approximate, and several vary by dialect.",
    },
    {
      heading: "Vowels",
      nav: {
        label: "Vowels",
        description: "Short and long (fada) vowel sounds.",
        example: "á → aw",
        icon: (
          <IconLetterCaseToggle
            size={NAV_ICON_SIZE}
            stroke={NAV_ICON_STROKE_WIDTH}
          />
        ),
      },
      body: "Short vowels, and their long (fada) counterparts.",
      content: <SoundTable rows={VOWELS} />,
    },
    {
      heading: "Vowel combinations",
      nav: {
        label: "Vowel combinations",
        description: "How vowel pairs and triples sound.",
        example: "ae → ay • aoi → e",
        icon: <IconAbc size={NAV_ICON_SIZE} stroke={NAV_ICON_STROKE_WIDTH} />,
      },
      body: "Pairs and triples of vowels have their own sounds. The first group is consistent; the rest vary more, especially by dialect.",
      content: <SoundTable rows={COMBINATIONS} />,
    },
    {
      heading: "Key slender consonants",
      nav: {
        label: "Slender consonants",
        description: "Consonants next to e/i take a slight 'y' glide.",
        example: "sí → shee • mise → mishe",
        icon: (
          <IconLetterS size={NAV_ICON_SIZE} stroke={NAV_ICON_STROKE_WIDTH} />
        ),
      },
      body: "Slender (palatalised) consonants sound as if a slight 'y' follows them. These are the most useful to master first.",
      content: <SoundTable rows={SLENDER_CONSONANTS} />,
    },
    {
      heading: "Séimhiú (lenition): the added h",
      nav: {
        label: "Lenition (séimhiú)",
        description: "An added h softens the consonant.",
        example: "bhí → vee • cara → chara",
        icon: (
          <IconFeather size={NAV_ICON_SIZE} stroke={NAV_ICON_STROKE_WIDTH} />
        ),
      },
      body: "An 'h' after a consonant softens its sound. The result differs in a broad vs a slender context. (Munster uses the same sound for bh and mh in either context.)",
      content: (
        <div className="space-y-4">
          <TransformList rows={SEIMHIU_TRANSFORMS} />
          <MutationTable rows={SEIMHIU} />
        </div>
      ),
    },
    {
      heading: "Urú (eclipsis)",
      nav: {
        label: "Eclipsis (urú)",
        description: "A prefixed letter suppresses the first.",
        example: "bord → mbord • conaí → i gconaí",
        icon: <IconMoon size={NAV_ICON_SIZE} stroke={NAV_ICON_STROKE_WIDTH} />,
      },
      body: "Urú adds a letter to the front of a word that suppresses the original first letter: you pronounce only the new one.",
      content: (
        <div className="space-y-4">
          <TransformList rows={URU_TRANSFORMS} />
          <EclipsisTable rows={URU} />
        </div>
      ),
    },
    {
      heading: "Dialects and stress",
      nav: {
        label: "Dialects & stress",
        description: "Sounds and stress vary by dialect.",
        example: "maith → mah / my",
        icon: (
          <IconMapPin size={NAV_ICON_SIZE} stroke={NAV_ICON_STROKE_WIDTH} />
        ),
      },
      body: "Pick one dialect (Munster, Connacht, or Ulster) and stick with it while learning. Stress usually falls on the first syllable, except in Munster, where a fada on a later syllable pulls the stress onto it (cupán, cipín). Recordings in the codex are tagged by dialect so you can match your ear to one.",
    },
  ],
}
